import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { orderStatuses } from 'features/Remittance/reportsListConfig';
import { ReactNode, useState } from 'react';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import FormDrawerComponent from 'ui-kit/components/drawer/FormDrawerComponent';
import InvoiceSummaryTable from 'ui-kit/components/payment/InvoiceSummaryTable';

interface PayDrawerProps {
  openDrawer: boolean;
  setOpenDrawer: (isOpen: boolean) => void;
  drawerData: any;
  setDrawerData: any;
  setSelectedRows: any;
  hasPayRight: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    alertContainer: {
      marginBottom: '63px',
    },
    alertMsg: {
      width: '-webkit-fill-available !important',
      maxWidth: 'unset !important',
      position: 'absolute',
      bottom: '63px',
      left: 0,
      padding: '0 35px',
      zIndex: 3,
    },
  }),
);

const PayDrawer = ({
  openDrawer,
  setOpenDrawer,
  drawerData,
  setDrawerData,
  setSelectedRows,
  hasPayRight,
}: PayDrawerProps): JSX.Element => {
  const classes = useStyles();
  const { orderDetails, payAndInvoiceData, payAndInvoiceStatus } = drawerData ?? {};
  const { orderNumber, orderStatus } = orderDetails ?? {};
  const [isErrorMsgVisible, showErrorMessage] = useState(false);

  const statusArray = [
    orderStatuses['open'],
    orderStatuses['inProcess'],
    orderStatuses['manualPay'],
    orderStatuses['wirePay'],
  ];

  const closeDrawer = () => {
    setOpenDrawer(false);
    setDrawerData({});
    showErrorMessage(false);
  };

  const handleExport = () => {
    setSelectedRows([{ ...orderDetails, initiateExport: true }]);
  };

  const viewInvoiceAndPay = () => {
    if (orderStatus === 'In Process') {
      showErrorMessage(true);
    } else {
      setSelectedRows([{ ...orderDetails, initiateInvoiceAndPay: true }]);
      closeDrawer();
    }
  };

  const actionItems: ReactNode = (
    <AgentNetButton color="primary" data-testid="InvoiceExport" onClick={handleExport} variant="outlined" size="small">
      Export
    </AgentNetButton>
  );

  return (
    <FormDrawerComponent
      title={`Order No: ${orderNumber}`}
      open={openDrawer}
      disableEnforceFocus
      primaryActionProps={{
        'data-testid': 'viewInvoiceAndPay',
      }}
      dismissActionProps={{
        'data-testid': 'viewInvoiceAndPayCancel',
      }}
      hidePrimaryAction={!hasPayRight && statusArray.includes(orderStatus ?? 'Open')}
      crossQAPrefix="RatesAndFees"
      {...(orderStatus && {
        primaryActionLabel: statusArray.includes(orderStatus) ? 'View Invoice & Pay' : 'View Summary',
      })}
      disabled={orderDetails?.isReopened}
      isPaddingRightZero
      actionItems={actionItems}
      onPrimaryAction={viewInvoiceAndPay}
      onDismissAction={closeDrawer}
      fullWidth
    >
      {payAndInvoiceStatus === 'loading' && (
        <div>
          <LoadingSpinner variant="circle" status={'pending'} />
        </div>
      )}
      {payAndInvoiceStatus === 'success' && (
        <InvoiceSummaryTable
          orderDate={payAndInvoiceData?.result?.orders?.[0]?.orderDate}
          underwriter={payAndInvoiceData?.result?.orders?.[0]?.invoices?.[0]?.underwriter}
          orderNumber={payAndInvoiceData?.result?.orders?.[0]?.orderNumber}
          invoices={payAndInvoiceData?.result?.orders?.[0]?.invoices}
          isTitleRequired={false}
          modalView
          showInvoiceNo
        />
      )}
      {isErrorMsgVisible && (
        <>
          <div className={classes.alertContainer}></div>
          <Alert className={classes.alertMsg} severity={'error'}>
            Orders can not be paid while Order status is In Process
          </Alert>
        </>
      )}
      {orderDetails?.isReopened && (
        <>
          <div className={classes.alertContainer}></div>
          <Alert className={classes.alertMsg} severity={'warning'}>
            Resubmitted files will not be eligible for payment until an invoice is generated, to ensure proper credits
            are applied.
          </Alert>
        </>
      )}
    </FormDrawerComponent>
  );
};

export default PayDrawer;
